import React from "react";
import { useNavigate } from "react-router-dom";
import IntegrationActivityLog from "../IntegrationActivityLog";

type Tab = "overview" | "activity";

export default function SkySwitch() {
  const [tab, setTab] = React.useState<Tab>("overview");

  const tabs: { key: Tab; label: string }[] = [
    { key: "overview", label: "Overview" },
    { key: "activity", label: "Activity" }
  ];

  return (
    <div className="p-6 max-w-4xl">
      <div className="flex items-center gap-4 mb-6">
        <div className="w-12 h-12 rounded-lg bg-sky-100 flex items-center justify-center text-2xl">📡</div>
        <div>
          <h1 className="text-xl font-bold text-slate-900 dark:text-white">SkySwitch</h1>
          <p className="text-sm text-slate-500">UCaaS / Hosted PBX — Screen Pop + Call Routing</p>
        </div>
      </div>

      <div className="mb-5 p-4 rounded-lg bg-sky-50 border border-sky-200 text-sm text-sky-800">
        <strong>Setup required:</strong> SkySwitch integration uses the SkySwitch reseller API to receive call events
        and trigger screen pops. Contact your Velaro account manager to begin setup — you will need your
        SkySwitch reseller credentials and sub-tenant identifiers.
      </div>

      <div className="flex border-b border-slate-200 mb-6 gap-1">
        {tabs.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${tab === t.key ? "border-primary text-primary" : "border-transparent text-slate-500 hover:text-slate-700"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === "overview" && (
        <div className="space-y-4">
          {[
            {
              icon: "📲", title: "Inbound call screen pop",
              desc: "When a SkySwitch call arrives at any extension, Velaro looks up the caller across your connected CRM and service tools. Customer name, open tickets, and account history appear in the agent sidebar before they answer."
            },
            {
              icon: "🏢", title: "Multi-tenant / reseller support",
              desc: "Built for SkySwitch resellers managing multiple sub-tenants. Each sub-tenant can be configured independently — one Velaro instance, many businesses."
            },
            {
              icon: "📋", title: "Call logging",
              desc: "Completed calls are automatically logged as activities on the matched CRM contact. Supports HubSpot, Salesforce, Dynamics 365, and Velaro CRM."
            },
            {
              icon: "🔄", title: "IVR integration",
              desc: "Calls handled by Velaro IVR can be transferred to SkySwitch extensions with full conversation context — agents see what the caller said before they connect."
            }
          ].map(item => (
            <div key={item.title} className="border border-slate-200 rounded-lg p-4 flex gap-3">
              <span className="text-2xl">{item.icon}</span>
              <div>
                <h3 className="font-semibold text-slate-900 dark:text-white mb-1">{item.title}</h3>
                <p className="text-sm text-slate-600">{item.desc}</p>
              </div>
            </div>
          ))}
          <div className="mt-6 p-4 rounded-lg bg-slate-50 border border-slate-200 text-sm text-slate-600">
            To get started, contact <strong>support@velaro.com</strong> or your account manager.
            SkySwitch reseller API access is required — your Velaro engineer will handle the webhook configuration.
          </div>
        </div>
      )}

      {tab === "activity" && <IntegrationActivityLog integrationSlug="SkySwitch" />}
    </div>
  );
}

export function BackToIntegrationLandingSkySwitch() {
  const navigate = useNavigate();
  return (
    <button onClick={() => navigate("/integrations")}
      className="text-sm text-slate-500 hover:text-slate-700 flex items-center gap-1">
      ← Integrations
    </button>
  );
}
